'use client';

import { useCallback, useRef, useSyncExternalStore } from 'react';

/** Tracks whether a DOM element (by selector) is within the viewport.
 *  Starts `true` so a dependent element stays hidden on first paint (SSR-safe,
 *  no flicker on pages that own the target). If the target is absent, resolves
 *  to `false` — treat it as "scrolled past". */
export function useElementInView(selector: string, rootMargin = '0px'): boolean {
    const inViewRef = useRef(true);

    const subscribe = useCallback(
        (onChange: () => void) => {
            const el = document.querySelector(selector);

            if (!el) {
                if (inViewRef.current) {
                    inViewRef.current = false;
                    onChange();
                }
                return () => {};
            }

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (inViewRef.current !== entry.isIntersecting) {
                        inViewRef.current = entry.isIntersecting;
                        onChange();
                    }
                },
                { rootMargin, threshold: 0 },
            );
            observer.observe(el);

            return () => observer.disconnect();
        },
        [selector, rootMargin],
    );

    return useSyncExternalStore(
        subscribe,
        () => inViewRef.current,
        () => true,
    );
}
